/**
 * Per-tab memory of viewer presentation state, keyed by study.
 *
 * Navigating viewer -> report template -> viewer (same tab, same study)
 * destroys the whole Cornerstone engine, so the hang the reader built —
 * layout, series assignments, rotation/flip/W-L/zoom/pan, current slice —
 * re-derives from scratch on return. This memory lets the same tab re-hang
 * the study exactly as it was left. It must NOT be persisted globally: the
 * next study (or the next reader on a shared workstation) has no business
 * inheriting one session's hang. sessionStorage keeps it per tab;
 * clearPresentationMemory() must be called from the auth logout block so a
 * shared workstation never replays one reader's hang to the next.
 *
 * Pane records are keyed by series.id (backend GUID, NOT seriesUid — split
 * buckets share a DICOM UID but are distinct series).
 *
 * Rotation, flips, W-L and the slice follow the SERIES into any pane. Zoom and
 * pan do not: they belong to the pane they were set in, at its size (see
 * `PaneGeometry`), and a series placed in any other pane opens fitted.
 */
const PRESENTATION_MEMORY_KEY = 'viewer.presentationMemory';
function isValidVoiRange(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return typeof v.lower === 'number' && typeof v.upper === 'number';
}
function isValidPan(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return typeof v.x === 'number' && typeof v.y === 'number';
}
function isValidGeometry(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return (typeof v.viewportId === 'string' && typeof v.width === 'number' && typeof v.height === 'number');
}
function isValidPane(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return ((v.geometry === undefined || v.geometry === null || isValidGeometry(v.geometry)) &&
        typeof v.rotation === 'number' &&
        typeof v.flipHorizontal === 'boolean' &&
        typeof v.flipVertical === 'boolean' &&
        typeof v.invert === 'boolean' &&
        (v.voiRange === null || isValidVoiRange(v.voiRange)) &&
        (v.zoom === null || typeof v.zoom === 'number') &&
        (v.pan === null || isValidPan(v.pan)) &&
        typeof v.imageIndex === 'number' &&
        Number.isInteger(v.imageIndex) &&
        v.imageIndex >= 0);
}
function isValidShell(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    if (typeof v.layout !== 'string' || typeof v.activeViewportId !== 'string')
        return false;
    if (typeof v.assignments !== 'object' || v.assignments === null || Array.isArray(v.assignments))
        return false;
    return Object.values(v.assignments).every((seriesId) => typeof seriesId === 'string');
}
function sanitizeStudy(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const v = value;
    const result = { panes: {} };
    if (isValidShell(v.shell))
        result.shell = v.shell;
    if (typeof v.panes === 'object' && v.panes !== null && !Array.isArray(v.panes)) {
        for (const [seriesId, pane] of Object.entries(v.panes)) {
            if (isValidPane(pane))
                result.panes[seriesId] = pane;
        }
    }
    if (!result.shell && Object.keys(result.panes).length === 0)
        return null;
    return result;
}
function readMap() {
    try {
        const raw = sessionStorage.getItem(PRESENTATION_MEMORY_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
            return {};
        const result = {};
        for (const [studyId, value] of Object.entries(parsed)) {
            const study = sanitizeStudy(value);
            if (study)
                result[studyId] = study;
        }
        return result;
    }
    catch {
        return {};
    }
}
function writeMap(map) {
    try {
        sessionStorage.setItem(PRESENTATION_MEMORY_KEY, JSON.stringify(map));
    }
    catch {
        // Storage unavailable — the hang simply won't restore; never throw.
    }
}
export function savePanePresentation(studyId, seriesId, pane) {
    const map = readMap();
    const entry = map[studyId] ?? { panes: {} };
    entry.panes[seriesId] = pane;
    map[studyId] = entry;
    writeMap(map);
}
export function peekPanePresentation(studyId, seriesId) {
    return readMap()[studyId]?.panes[seriesId] ?? null;
}
/**
 * Re-point a remembered pane at another slice, keeping the rest of its
 * presentation. For callers that assign a series in order to land on ONE slice
 * (the echo panel's jump to a saved measurement): a pane record wins over the
 * viewport's imageIndex when the series mounts, so a series this tab has shown
 * before would otherwise resume where the reader left it, not where they asked
 * to go. With no record there is nothing to override and nothing is written.
 */
export function setPanePresentationImageIndex(studyId, seriesId, imageIndex) {
    const map = readMap();
    const pane = map[studyId]?.panes[seriesId];
    if (!pane)
        return;
    pane.imageIndex = Math.max(0, Math.trunc(imageIndex));
    writeMap(map);
}
export function saveShellPresentation(studyId, shell) {
    const map = readMap();
    const entry = map[studyId] ?? { panes: {} };
    entry.shell = shell;
    map[studyId] = entry;
    writeMap(map);
}
export function peekShellPresentation(studyId) {
    return readMap()[studyId]?.shell ?? null;
}
/**
 * Drop only the shell record (layout + assignments) for a study, keeping the
 * per-series pane records. Used when the departing hang must NOT be replayed
 * structurally (e.g. a comparison hang holding prior-study series) but the
 * per-pane presentation is still worth restoring per series.
 */
export function forgetShellPresentation(studyId) {
    const map = readMap();
    const entry = map[studyId];
    if (!entry?.shell)
        return;
    delete entry.shell;
    if (Object.keys(entry.panes).length === 0)
        delete map[studyId];
    writeMap(map);
}
export function clearPresentationMemory() {
    try {
        sessionStorage.removeItem(PRESENTATION_MEMORY_KEY);
    }
    catch {
        // Storage unavailable — nothing was stored anyway.
    }
}
function paneGeometry(viewport) {
    const { id, canvas } = viewport;
    if (!id || !canvas?.width || !canvas.height)
        return null;
    return { viewportId: id, width: canvas.width, height: canvas.height };
}
/** Same pane, same size (to a couple of device pixels: layout maths rounds). */
function isSamePane(saved, here) {
    if (!saved || !here)
        return false;
    return (saved.viewportId === here.viewportId &&
        Math.abs(saved.width - here.width) <= 2 &&
        Math.abs(saved.height - here.height) <= 2);
}
function normalizePan(pan) {
    if (Array.isArray(pan) && typeof pan[0] === 'number' && typeof pan[1] === 'number') {
        return { x: pan[0], y: pan[1] };
    }
    if (isValidPan(pan))
        return { x: pan.x, y: pan.y };
    return null;
}
/** Read a pane's full presentation. Returns null if the viewport throws (torn down). */
export function capturePanePresentation(viewport) {
    try {
        const { rotation = 0 } = viewport.getViewPresentation();
        const camera = viewport.getCamera();
        const properties = viewport.getProperties();
        const zoom = viewport.getZoom();
        const imageIndex = viewport.getCurrentImageIdIndex();
        return {
            rotation,
            flipHorizontal: camera.flipHorizontal ?? false,
            flipVertical: camera.flipVertical ?? false,
            invert: properties.invert ?? false,
            voiRange: properties.voiRange
                ? { lower: properties.voiRange.lower, upper: properties.voiRange.upper }
                : null,
            zoom: Number.isFinite(zoom) && zoom > 0 ? zoom : null,
            pan: normalizePan(viewport.getPan()),
            imageIndex: Number.isInteger(imageIndex) && imageIndex >= 0 ? imageIndex : 0,
            geometry: paneGeometry(viewport),
        };
    }
    catch {
        return null;
    }
}
/**
 * Re-apply a captured presentation. Does not render and does not touch the
 * image index (the index is restored via setStack's initialIndex so the first
 * decoded frame is already the right slice). Zoom and pan only in the pane, at
 * the size, they were captured in: anywhere else the pane keeps its fit.
 */
export function applyPanePresentation(viewport, pane) {
    try {
        viewport.setViewPresentation({ rotation: pane.rotation });
        viewport.setCamera({ flipHorizontal: pane.flipHorizontal, flipVertical: pane.flipVertical });
        if (pane.voiRange) {
            viewport.setProperties({ invert: pane.invert, voiRange: { ...pane.voiRange } });
        }
        else {
            viewport.setProperties({ invert: pane.invert });
        }
        if (!isSamePane(pane.geometry, paneGeometry(viewport)))
            return;
        if (pane.zoom !== null)
            viewport.setZoom(pane.zoom);
        if (pane.pan !== null)
            viewport.setPan([pane.pan.x, pane.pan.y]);
    }
    catch {
        // A restore must never break stack setup — worst case the pane opens fresh.
    }
}
